import { Component, computed, effect, input, output, signal } from '@angular/core';
import { CountryDefinition, EnseigneConfig } from '../../enseigne.model';
import { UiModalComponent } from '../../../../shared/components/ui-modal/ui-modal.component';
import { UiSectionComponent } from '../../../../shared/components/ui-block/components/ui-section.component';
import { TranslatePipe } from '@ngx-translate/core';
import { UiBlockComponent } from '../../../../shared/components/ui-block/ui-block.component';

const defaultCountryDefinition: CountryDefinition = {
  country: '',
  salesforceRoutingCode: '',
  sapRoutingCode: '',
  prefix: '',
  transportGroups: [],
};

@Component({
  selector: 'app-enseigne-form-modal',
  imports: [UiModalComponent, UiSectionComponent, TranslatePipe, UiBlockComponent],
  templateUrl: './enseigne-form-modal.component.html',
  styleUrl: './enseigne-form-modal.component.scss',
})
export class EnseigneFormModalComponent {
  readonly availableCountryCodes = input<string[]>([]);
  readonly enseigne = input<EnseigneConfig | null>(null);

  readonly enseigneSubmit = output<EnseigneConfig>();
  readonly enseigneCancel = output<void>();

  readonly isEditMode = computed(() => this.enseigne() !== null);

  readonly submitAttempted = signal(false);

  readonly formData = signal<EnseigneConfig>({
    enseigne: '',
    codeOrg: '',
    countriesConfig: [{ ...defaultCountryDefinition }],
  });

  errors = computed(() => {
    const data = this.formData();
    return {
      enseigne: data.enseigne.trim() ? null : 'REQUIRED',
      codeOrg: data.codeOrg.trim() ? null : 'REQUIRED',
      countriesConfig: data.countriesConfig.map((c) => ({
        country: c.country.trim() ? null : 'REQUIRED',
        prefix: c.prefix.trim() ? null : 'REQUIRED',
      })),
    };
  });

  isFormValid = computed(() => {
    const e = this.errors();
    return !e.enseigne && !e.codeOrg && e.countriesConfig.every((c) => !c.country && !c.prefix);
  });

  constructor() {
    effect(
      () => {
        const ens = this.enseigne();
        if (ens) {
          this.formData.set({
            ...ens,
            countriesConfig: ens.countriesConfig.map((c) => ({
              ...defaultCountryDefinition,
              ...c,
              transportGroups: c.transportGroups ?? [],
            })),
          });
        } else {
          this.formData.set({
            enseigne: '',
            codeOrg: '',
            countriesConfig: [{ ...defaultCountryDefinition }],
          });
        }
        this.submitAttempted.set(false);
      },
      { allowSignalWrites: true },
    );
  }

  onSubmit(): void {
    this.submitAttempted.set(true);
    if (!this.isFormValid()) return;
    this.enseigneSubmit.emit(this.formData());
  }

  onCancel(): void {
    this.submitAttempted.set(false);
    this.enseigneCancel.emit();
  }

  updateField(field: keyof EnseigneConfig, value: string | string[]): void {
    this.formData.update((data) => ({ ...data, [field]: value }));
  }

  onAddCountry(): void {
    this.formData.update((data) => ({
      ...data,
      countriesConfig: [...data.countriesConfig, { ...defaultCountryDefinition }],
    }));
  }

  onRemoveCountry(index: number): void {
    this.formData.update((data) => ({
      ...data,
      countriesConfig: data.countriesConfig.filter((_, i) => i !== index),
    }));
  }

  updateCountryField(index: number, field: keyof CountryDefinition, value: string | string[]): void {
    this.formData.update((data) => {
      const countriesConfig = [...data.countriesConfig];
      countriesConfig[index] = { ...countriesConfig[index], [field]: value };
      return { ...data, countriesConfig };
    });
  }

  updateCountryTransportGroups(index: number, value: string): void {
    const groups = value
      .split(',')
      .map((g) => g.trim())
      .filter((g) => g.length > 0);
    this.updateCountryField(index, 'transportGroups', groups);
  }
}
